'use client';

import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useState } from 'react';

import { Flex } from '@/components/common/Flex';
import { Link } from '@/components/common/Link';
import { MarketingPrivacyPolicy, MarketingSiteHome } from '@/constants/urls/urls';

import { COOKIE_BANNER_DESKTOP_MEDIA_QUERY } from './constants';
import {
  type CookieConsentPreferences,
  dispatchCookieConsentChanged,
  getEffectiveCookieConsentPreferences,
  IMPLICIT_COOKIE_CONSENT_FULL,
  persistCookieConsent,
  pushConsentToGtag,
  readStoredCookieConsent,
} from './cookieConsentStorage';
import { CookieSettingsDialog } from './CookieSettingsDialog';

import styles from './CookieConsentBanner.module.scss';

const rejectPreferences: CookieConsentPreferences = {
  analytics: false,
  marketing: false,
};

const acceptAllPreferences: CookieConsentPreferences = { ...IMPLICIT_COOKIE_CONSENT_FULL };

export const CookieConsentBanner: FC = () => {
  const t = useTranslations('cookieConsent');
  const [showBanner, setShowBanner] = useState(false);
  const [showDecoration, setShowDecoration] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COOKIE_BANNER_DESKTOP_MEDIA_QUERY).matches
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitial, setSettingsInitial] = useState<CookieConsentPreferences>(IMPLICIT_COOKIE_CONSENT_FULL);

  useEffect(() => {
    const mq = window.matchMedia(COOKIE_BANNER_DESKTOP_MEDIA_QUERY);

    const sync = () => setShowDecoration(mq.matches);

    sync();
    mq.addEventListener('change', sync);

    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const stored = readStoredCookieConsent();

    if (!stored) {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    if (!showBanner) {
      setSettingsOpen(false);
    }
  }, [showBanner]);

  const commitConsent = useCallback((prefs: CookieConsentPreferences) => {
    persistCookieConsent(prefs);
    dispatchCookieConsentChanged(prefs);
    pushConsentToGtag(prefs);
    setShowBanner(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    commitConsent(acceptAllPreferences);
  }, [commitConsent]);

  const handleRejectAll = useCallback(() => {
    commitConsent(rejectPreferences);
  }, [commitConsent]);

  const handleOpenSettings = useCallback(() => {
    setSettingsInitial(getEffectiveCookieConsentPreferences());
    setSettingsOpen(true);
  }, []);

  if (!showBanner) {
    return (
      <CookieSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialPreferences={settingsInitial}
        onSave={commitConsent}
      />
    );
  }

  return (
    <>
      <aside className={styles.root} role="dialog" aria-label={t('ariaLabel')} aria-live="polite">
        {showDecoration ? (
          <div className={styles.background} aria-hidden>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/cookie-consent-bg.svg" alt="" className={styles.bgSliceLeft} draggable={false} />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/cookie-consent-bg.svg" alt="" className={styles.bgSliceRight} draggable={false} />
          </div>
        ) : null}

        <div className={styles.inner}>
          <Flex className={styles.copy} direction="column" gap={6}>
            <p className={styles.message}>
              {t.rich('message', {
                siteLink: (chunks) => (
                  <Link href={MarketingSiteHome} isOutsideLink className={styles.inlineLink}>
                    {chunks}
                  </Link>
                ),
                cookiesLink: (chunks) => (
                  <Link
                    href={MarketingPrivacyPolicy}
                    isOutsideLink
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.inlineLink}
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>

            <p className={styles.privacyLine}>
              {t.rich('privacyLine', {
                privacyLink: (chunks) => (
                  <Link href={MarketingPrivacyPolicy} isOutsideLink target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </Link>
                ),
              })}
            </p>
          </Flex>

          <div className={styles.actions}>
            <button type="button" className={styles.btnAccept} onClick={handleAcceptAll}>
              {t('acceptAll')}
            </button>

            <button type="button" className={styles.btnSecondary} onClick={handleRejectAll}>
              {t('rejectAll')}
            </button>

            <button type="button" className={styles.btnSecondary} onClick={handleOpenSettings}>
              {t('cookieSettings')}
            </button>
          </div>
        </div>
      </aside>

      <CookieSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialPreferences={settingsInitial}
        onSave={commitConsent}
      />
    </>
  );
};
